import React, { useEffect, useMemo, useState } from 'react'
import { MdDelete, MdEmail, MdOpenInNew } from 'react-icons/md'
import { FaPhoneAlt } from 'react-icons/fa'
import PopupDropdown from '../PopupDropdown'
import LoadingSpinner from '../placeholders/LoadingSpinner'
import './DedupeWorkspace.css'

const CONFIDENCE_OPTIONS = [
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
]

const CONFIDENCE_LABELS = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

function DedupeWorkspace({
  sectionTitle,
  entityLabel,
  entityLabelPlural,
  records,
  groups,
  loading,
  onMerge,
  onRefresh,
  onOpenRecord,
  selectedGroupId,
  onSelectGroup,
  getRecordTitle,
  getRecordSubtitle,
  getRecordFacts,
}) {
  const [selectedConfidence, setSelectedConfidence] = useState(['high', 'medium'])
  const [manualGroups, setManualGroups] = useState([])
  const [groupOverrides, setGroupOverrides] = useState({})
  const [mergeSaving, setMergeSaving] = useState(false)

  const recordsById = useMemo(() => Object.fromEntries(records.map(record => [record.id, record])), [records])
  const baseGroups = useMemo(() => [...groups, ...manualGroups], [groups, manualGroups])
  const resolvedGroups = useMemo(() => baseGroups.map(group => groupOverrides[group.id] || group), [baseGroups, groupOverrides])

  const visibleGroups = useMemo(() => {
    return resolvedGroups.filter(group => !group.auto || selectedConfidence.includes(group.confidence))
  }, [resolvedGroups, selectedConfidence])

  const selectedGroup = useMemo(() => resolvedGroups.find(group => group.id === selectedGroupId) || null, [resolvedGroups, selectedGroupId])

  const selectedGroupMembers = useMemo(() => {
    if (!selectedGroup) return []
    return (selectedGroup.member_ids || [])
      .map(id => recordsById[id])
      .filter(Boolean)
      .sort((left, right) => (right.relation_count_total || 0) - (left.relation_count_total || 0) || left.id - right.id)
  }, [recordsById, selectedGroup])

  const effectiveSurvivorId = useMemo(() => {
    if (!selectedGroup) return null
    if (selectedGroup.survivor_id) return selectedGroup.survivor_id
    return selectedGroupMembers[0]?.id || null
  }, [selectedGroup, selectedGroupMembers])

  const addableOptions = useMemo(() => {
    if (!selectedGroup) return []

    return records
      .filter(record => !selectedGroup.member_ids.includes(record.id))
      .map(record => ({
        id: record.id,
        name: `${getRecordTitle(record)} ${getRecordSubtitle(record) ? `• ${getRecordSubtitle(record)}` : ''}`.trim(),
      }))
  }, [getRecordSubtitle, getRecordTitle, records, selectedGroup])

  useEffect(() => {
    if (selectedGroupId && !resolvedGroups.some(group => group.id === selectedGroupId)) {
      onSelectGroup(null)
    }
  }, [resolvedGroups, selectedGroupId])

  useEffect(() => {
    if (!selectedGroup || selectedGroup.survivor_id || selectedGroupMembers.length === 0) return

    const defaultSurvivorId = selectedGroupMembers[0].id
    setGroupOverrides(prev => ({
      ...prev,
      [selectedGroup.id]: {
        ...selectedGroup,
        survivor_id: defaultSurvivorId,
      }
    }))
    if (!selectedGroup.auto) {
      setManualGroups(prev => prev.map(group => group.id === selectedGroup.id ? { ...group, survivor_id: defaultSurvivorId } : group))
    }
  }, [selectedGroup, selectedGroupMembers])

  const applyGroupUpdate = (groupId, updater) => {
    const currentGroup = resolvedGroups.find(group => group.id === groupId)
    if (!currentGroup) return

    const nextGroup = updater(currentGroup)
    setGroupOverrides(prev => ({ ...prev, [groupId]: nextGroup }))

    if (!nextGroup.auto) {
      setManualGroups(prev => prev.map(group => group.id === groupId ? nextGroup : group))
    }
  }

  const handleToggleConfidence = (confidenceId) => {
    setSelectedConfidence(prev => {
      if (prev.includes(confidenceId)) {
        if (prev.length === 1) return prev
        return prev.filter(item => item !== confidenceId)
      }

      return [...prev, confidenceId]
    })
  }

  const handleCreateManualGroup = () => {
    const groupId = `manual-${Date.now()}`
    const nextGroup = {
      id: groupId,
      auto: false,
      confidence: 'manual',
      match_reasons: ['Manual review group'],
      member_ids: [],
      size: 0,
      linked_data_total: 0,
      title: 'Manual group',
      survivor_id: null,
    }

    setManualGroups(prev => [nextGroup, ...prev])
    setGroupOverrides(prev => ({ ...prev, [groupId]: nextGroup }))
    onSelectGroup(groupId)
  }

  const handleToggleGroup = (groupId) => {
    onSelectGroup(selectedGroupId === groupId ? null : groupId)
  }

  const handleAddRecordToGroup = (option) => {
    if (!selectedGroup || !option?.id) return
    const record = recordsById[option.id]
    if (!record) return

    applyGroupUpdate(selectedGroup.id, currentGroup => {
      const memberIds = currentGroup.member_ids.includes(record.id)
        ? currentGroup.member_ids
        : [...currentGroup.member_ids, record.id]

      return {
        ...currentGroup,
        member_ids: memberIds,
        size: memberIds.length,
        linked_data_total: memberIds.reduce((total, id) => total + (recordsById[id]?.relation_count_total || 0), 0),
        survivor_id: currentGroup.survivor_id || record.id,
      }
    })
  }

  const handleRemoveRecordFromGroup = (recordId) => {
    if (!selectedGroup) return

    applyGroupUpdate(selectedGroup.id, currentGroup => {
      const memberIds = currentGroup.member_ids.filter(id => id !== recordId)
      const sortedRemainingMembers = memberIds
        .map(id => recordsById[id])
        .filter(Boolean)
        .sort((left, right) => (right.relation_count_total || 0) - (left.relation_count_total || 0) || left.id - right.id)

      return {
        ...currentGroup,
        member_ids: memberIds,
        size: memberIds.length,
        linked_data_total: memberIds.reduce((total, id) => total + (recordsById[id]?.relation_count_total || 0), 0),
        survivor_id: currentGroup.survivor_id === recordId ? (sortedRemainingMembers[0]?.id || null) : currentGroup.survivor_id,
      }
    })
  }

  const handleSelectSurvivor = (recordId) => {
    if (!selectedGroup) return
    applyGroupUpdate(selectedGroup.id, currentGroup => ({ ...currentGroup, survivor_id: recordId }))
  }

  const handleMerge = async () => {
    if (!selectedGroup || selectedGroup.member_ids.length < 2 || !effectiveSurvivorId) return

    setMergeSaving(true)
    try {
      await onMerge({
        survivorId: effectiveSurvivorId,
        mergedIds: selectedGroup.member_ids.filter(id => id !== effectiveSurvivorId),
      })
      setManualGroups([])
      setGroupOverrides({})
      onSelectGroup(null)
      await onRefresh()
    } finally {
      setMergeSaving(false)
    }
  }

  return (
    <>
      <div className='body__section big dedupe-workspace__main'>
        <div className='dedupe-workspace__title-row'>
          <h2>{sectionTitle}</h2>
          <button type='button' className='primary-button-header dedupe-workspace__new-group-button' onClick={handleCreateManualGroup}>
            New group
          </button>
        </div>

        <div className='dedupe-workspace__section-header'>
          <div className='dedupe-workspace__filter-group'>
            {CONFIDENCE_OPTIONS.map(option => (
              <button
                key={option.id}
                type='button'
                className={`dedupe-workspace__confidence-button ${option.id} ${selectedConfidence.includes(option.id) ? 'active' : ''}`}
                onClick={() => handleToggleConfidence(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? <LoadingSpinner /> : null}

        {!loading && visibleGroups.length === 0 ? <div className='dedupe-workspace__empty'>No groups found for the active filters.</div> : null}

        {!loading && visibleGroups.length > 0 ? (
          <div className='table dedupe-workspace__table'>
            <div className='dedupe-workspace__table-header'>
              <div className='table__header--field'>Level</div>
              <div className='table__header--field'>Group</div>
              <div className='table__header--field'>Match reason</div>
              <div className='table__header--field'>Linked</div>
              <div className='table__header--field'>Items</div>
            </div>

            <div className='table__rows_container'>
              {visibleGroups.map(group => (
                <button
                  key={group.id}
                  type='button'
                  className={`dedupe-workspace__table-row ${selectedGroupId === group.id ? 'active' : ''}`}
                  onClick={() => handleToggleGroup(group.id)}
                >
                  <div className='table__row--field'>
                    <span className={`dedupe-workspace__badge ${group.auto ? `confidence-${group.confidence}` : 'manual'}`}>
                      {group.auto ? CONFIDENCE_LABELS[group.confidence] : 'Manual'}
                    </span>
                  </div>
                  <div className='table__row--field'>{group.title || 'Review group'}</div>
                  <div className='table__row--field'>{group.match_reasons?.join(' • ') || 'Manual review group'}</div>
                  <div className='table__row--field'>{group.linked_data_total || 0}</div>
                  <div className='table__row--field'>{group.member_ids.length}</div>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className={`body__section ${selectedGroup ? 'expanded' : 'collapsed'}`} id='dedupe-detail-info'>
        {selectedGroup ? (
          <>
              <div className='dedupe-detail-info__header'>
                <div className='dedupe-detail-info__header-copy'>
                  <h2>{selectedGroup.auto ? 'DUPLICATE DETAILS' : 'MANUAL GROUP'}</h2>
                  <p>{selectedGroup.member_ids.length} {entityLabelPlural} • {selectedGroup.linked_data_total || 0} linked records</p>
                </div>

                <button
                  type='button'
                  className='primary-button-header'
                  disabled={mergeSaving || selectedGroup.member_ids.length < 2 || !effectiveSurvivorId}
                  onClick={handleMerge}
                >
                  {mergeSaving ? `Merging...` : `Merge ${entityLabelPlural}`}
                </button>
              </div>

              <div className='dedupe-detail-info__content'>
                <div className='info-block'>
                  <h3 className='info-block__title'>Match</h3>
                  <div className='info-details'>
                    <div className='info-item'>
                      <span className='info-label'>Level:</span>
                      <span className='info-value'>{selectedGroup.auto ? CONFIDENCE_LABELS[selectedGroup.confidence] : 'Manual'}</span>
                    </div>
                    <div className='info-item dedupe-detail-info__reasons'>
                      <span className='info-label'>Reason:</span>
                      <span className='info-value'>{selectedGroup.match_reasons?.join(' • ') || 'Manual review group'}</span>
                    </div>
                  </div>
                </div>

                <div className='pocs-section dedupe-detail-info__contacts-section'>
                  <div className='pocs-header'>
                    <h3>{entityLabelPlural.charAt(0).toUpperCase() + entityLabelPlural.slice(1)}</h3>
                    <div className='icon-button'>
                      <PopupDropdown
                        title='+'
                        options={addableOptions}
                        onChange={handleAddRecordToGroup}
                        searchBar={true}
                      />
                    </div>
                  </div>

                  <div className='poc-list'>
                    {selectedGroupMembers.length === 0 ? (
                      <div className='no-pocs'>No {entityLabelPlural} added</div>
                    ) : (
                      selectedGroupMembers.map(record => (
                        <div
                          className={`poc-item dedupe-detail-info__record-item ${effectiveSurvivorId === record.id ? 'is-survivor' : ''}`}
                          key={record.id}
                          onClick={() => handleSelectSurvivor(record.id)}
                        >
                          <div>
                            {getRecordTitle(record)}
                            {effectiveSurvivorId === record.id ? <span className='dedupe-detail-info__survivor-badge active'>Survivor</span> : <span className='dedupe-detail-info__survivor-badge'>Select as survivor</span>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <MdEmail /> {getRecordSubtitle(record) || 'No email'}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaPhoneAlt /> {(getRecordFacts(record) || [])[1] || `${record.relation_count_total || 0} linked records`}
                          </div>
                          <div className='poc-item__actions'>
                            <button className='poc-copy-button' onClick={(event) => { event.stopPropagation(); onOpenRecord(record.id) }}>
                              <MdOpenInNew />
                            </button>
                            <button className='poc-delete-button' onClick={(event) => { event.stopPropagation(); handleRemoveRecordFromGroup(record.id) }}>
                              <MdDelete />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
          </>
        ) : null}
      </div>
    </>
  )
}

export default DedupeWorkspace
